import { useSyncExternalStore } from "react";
import type { ConfirmOptions } from "../contexts/ConfirmContext";

/** An AI button that is working: what it locks and since when. */
export interface AiJob {
    id: number;
    button: HTMLButtonElement;
    /** The button's own words, e.g. "Write the first draft". */
    label: string;
    /** The panel, drawer body or dialog body it works on; null when the button sits on its own. */
    scope: HTMLElement | null;
    startedAt: number;
}

let jobs: AiJob[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = (): void => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

const SCOPE = "[data-ai-scope], .side-drawer-body, .side-drawer, .modal-body, .modal-content";

/** The area an AI button works on: its panel, or the body of its drawer or dialog. */
export const findAiScope = (button: HTMLElement): HTMLElement | null => {
    const scope = button.closest<HTMLElement>(SCOPE);
    // a button in a drawer's or dialog's footer works on its body
    return scope?.querySelector<HTMLElement>(":scope > .side-drawer-body, :scope > .modal-body") ?? scope;
};

/** The button's words without its "AI · N left" mark. */
export const aiLabelOf = (button: HTMLElement): string => {
    const copy = button.cloneNode(true) as HTMLElement;
    copy.querySelectorAll("[data-ai-mark]").forEach((mark) => mark.remove());
    return (copy.textContent ?? "").replace(/\s+/g, " ").trim();
};

/** Registers a working AI button; the returned function ends it. */
export const startAiJob = (button: HTMLButtonElement, label: string): (() => void) => {
    const job: AiJob = { id: nextId++, button, label, scope: findAiScope(button), startedAt: Date.now() };
    jobs = [...jobs, job];
    emit();
    return () => {
        jobs = jobs.filter((j) => j !== job);
        emit();
    };
};

export const getAiJobs = (): AiJob[] => jobs;

/** The AI buttons working right now (one at a time in practice). */
export const useAiJobs = (): AiJob[] => useSyncExternalStore(subscribe, getAiJobs);

/**
 * While the AI works, other AI buttons and the page's main buttons wait — a
 * dialog's main button only when the AI works inside that dialog, so an
 * unrelated confirm (e.g. "Leave this page?") still works.
 */
export const aiButtonWaits = (button: HTMLElement | null, current: AiJob[], isAi: boolean, isMain: boolean): boolean => {
    if (!button || !current.length || current.some((job) => job.button === button)) return false;
    if (isAi) return true;
    if (!isMain) return false;
    const dialog = button.closest(".modal");
    return !dialog || current.some((job) => dialog.contains(job.button));
};

/** The confirm shown when someone leaves while the AI works. */
export const aiLeavePrompt = (label: string, hasUnsavedChanges = false): ConfirmOptions & { message: string } => ({
    titleVerb: "Leave",
    entity: "this page?",
    titleIcon: "bi-stars",
    message: `The AI is still working on “${label}”. If you leave now, you won't see the result here.${hasUnsavedChanges ? " Your unsaved changes are lost too." : ""}`,
    confirmLabel: "Leave",
    cancelLabel: "Stay",
});
